import * as React from 'react'

import { Popover as PopoverPrimitive } from '@base-ui/react/popover'

import type { PopoverContent as PopoverContentBase } from '@/components/ui/popover'
import {
  Popover,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface PopoverContentProps extends React.ComponentProps<
  typeof PopoverContentBase
> {
  /**
   * When true, disables the Portal and renders the content inline.
   * Useful when Popover is inside a Drawer/Dialog to prevent click-outside issues.
   */
  disablePortal?: boolean
}

function PopoverContent({
  className,
  align = 'center',
  alignOffset = 0,
  side = 'bottom',
  sideOffset = 4,
  disablePortal = false,
  ...props
}: PopoverContentProps) {
  const containerRef = React.useRef<HTMLDivElement>(null)

  const content = (
    <PopoverPrimitive.Positioner
      align={align}
      alignOffset={alignOffset}
      side={side}
      sideOffset={sideOffset}
      className="isolate z-50"
    >
      <PopoverPrimitive.Popup
        data-slot="popover-content"
        className={cn(
          'bg-popover text-popover-foreground data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 ring-foreground/10 z-50 flex w-72 origin-(--transform-origin) flex-col gap-4 rounded-md p-4 text-sm shadow-md ring-1 outline-hidden duration-100',
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Positioner>
  )

  if (disablePortal) {
    return (
      <>
        <div ref={containerRef} />
        <PopoverPrimitive.Portal container={containerRef}>
          {content}
        </PopoverPrimitive.Portal>
      </>
    )
  }

  return <PopoverPrimitive.Portal>{content}</PopoverPrimitive.Portal>
}

export {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
}
